// Shared types and empty initial state for the Web Hailing flow

export interface VehicleClass {
  key: string;
  name: string;
  blurb: string;
  seats: number;
  bags: number;
  eta: number;
  meter: number;
  flat: number;
  price: string;
  quoteOptionKey?: string;
  quoteId?: string;
  vehicleClassCode?: string;
  operatingArea?: string[];
  fareType?: string;
  isFixedFare?: boolean;
  discountedTotal?: number;
  total?: number;
  iconUrl?: string;
}

export interface HailingStepData {
  currentStep: string;
  pickupPlace: string;
  pickupAddress: string;
  /** Real place id from place-details (empty until a live pick resolves). */
  pickupPlaceId: string;
  pickupLat: number | null;
  pickupLng: number | null;
  dropoffPlace: string;
  dropoffAddress: string;
  /** Real place id from place-details (empty until a live pick resolves). */
  dropoffPlaceId: string;
  dropoffLat: number | null;
  dropoffLng: number | null;
  stopPlace: string;
  stopAddress: string;
  stopPlaceId: string;
  stopLat: number | null;
  stopLng: number | null;
  /** Pickup time of a scheduled ride (ISO); null books one now. */
  scheduledTime: string | null;
  /** The most expensive selected class: its fare is the "up to" amount shown and held. */
  vehicleClass: VehicleClass;
  /** Every class the rider selected (same fare kind); unset means just `vehicleClass`. */
  vehicleClasses?: VehicleClass[];
  fareKind: "meter" | "flat";
  fareEstimate: string;
  paymentMethod: PaymentMethod;
  hailingRequestId?: string;
  routePolyline?: string;
  driverPosition?: { lat: number; lng: number };
  driverOperatingArea?: string;
}

export interface PaymentMethod {
  id: string;
  name: string;
  sub: string;
  type: "card";
}

export const INITIAL_HAILING_STATE: HailingStepData = {
  currentStep: "where",
  pickupPlace: "",
  pickupAddress: "",
  pickupPlaceId: "",
  pickupLat: null,
  pickupLng: null,
  // Drop-off starts empty — the field is filled by picking a search result
  // (mockup "where" panel); the route panel appears once both fields are set.
  dropoffPlace: "",
  dropoffAddress: "",
  dropoffPlaceId: "",
  dropoffLat: null,
  dropoffLng: null,
  stopPlace: "",
  stopAddress: "",
  stopPlaceId: "",
  stopLat: null,
  stopLng: null,
  scheduledTime: null,
  vehicleClass: {
    key: "",
    name: "",
    blurb: "",
    seats: 0,
    bags: 0,
    eta: 0,
    meter: 0,
    flat: 0,
    price: "",
  },
  fareKind: "meter",
  fareEstimate: "",
  paymentMethod: { id: "", name: "", sub: "", type: "card" },
};

/** The classes the rider selected, the single `vehicleClass` when no list is set. */
export const selectedVehicleClasses = (state: Pick<HailingStepData, "vehicleClass" | "vehicleClasses">): VehicleClass[] =>
  state.vehicleClasses?.length ? state.vehicleClasses : [state.vehicleClass];

/** More than one class selected: fares read "up to" the highest. */
export const hasMultipleVehicleClasses = (state: Pick<HailingStepData, "vehicleClass" | "vehicleClasses">): boolean =>
  (state.vehicleClasses?.length ?? 0) > 1;
